import { useState } from 'react';
import { useInView } from '../hooks/useInView';

interface DiagramNode {
  id: string;
  label: string;
  sublabel: string;
  detail: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  stroke: string;
}

const nodes: DiagramNode[] = [
  {
    id: 'apps',
    label: 'Applications',
    sublabel: 'SaaS · Internal Tools · APIs',
    detail:
      'Your existing applications connect to Opair using a single SDK or API endpoint change. No code modifications needed — just point your AI client to the Opair Gateway.',
    x: 30,
    y: 120,
    width: 180,
    height: 90,
    color: '#1e1b4b',
    stroke: '#4338ca',
  },
  {
    id: 'gateway',
    label: 'Opair Gateway',
    sublabel: 'Policy · Detect · Route · Audit',
    detail:
      'The data plane that intercepts every AI request. Applies policies, detects sensitive data, masks PII, routes to optimal providers, and generates tamper-evident audit logs — all in <10ms p99.',
    x: 310,
    y: 90,
    width: 230,
    height: 110,
    color: '#312e81',
    stroke: '#6366f1',
  },
  {
    id: 'providers',
    label: 'AI Providers',
    sublabel: 'OpenAI · Anthropic · Azure · Vertex AI · Bedrock',
    detail:
      'Multi-provider support with intelligent routing. Opair selects the best model based on cost, latency, capability, and compliance requirements, with automatic fallback on failure.',
    x: 640,
    y: 120,
    width: 180,
    height: 90,
    color: '#1e1b4b',
    stroke: '#4338ca',
  },
  {
    id: 'control',
    label: 'Control Plane',
    sublabel: 'Policies · Config · Analytics · Console',
    detail:
      'The management layer where admins define policies, configure routing rules, view real-time analytics, and manage compliance. Accessible via the Opair Console or REST API.',
    x: 330,
    y: 280,
    width: 190,
    height: 80,
    color: '#1e1b4b',
    stroke: '#4338ca',
  },
];

export function ArchitectureDiagram() {
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [ref, isInView] = useInView();

  const active = nodes.find((n) => n.id === activeNode);

  return (
    <section
      id="architecture"
      className="relative py-24 sm:py-32 bg-gray-950"
      aria-labelledby="architecture-heading"
    >
      <div
        className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            id="architecture-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
          >
            How <span className="gradient-text">Opair</span> works
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            Opair sits transparently between your applications and AI providers as a governance
            layer. Click on any component to learn more.
          </p>
        </div>

        {/* Interactive SVG */}
        <div className="flex justify-center">
          <svg
            viewBox="0 0 850 400"
            className="w-full max-w-4xl"
            role="img"
            aria-label="Interactive architecture diagram. Click components for details."
          >
            {/* Connection lines */}
            <line
              x1="210"
              y1="165"
              x2="310"
              y2="145"
              stroke="#4f46e5"
              strokeWidth="2"
              strokeDasharray="6 4"
              opacity="0.4"
            />
            <line
              x1="540"
              y1="145"
              x2="640"
              y2="165"
              stroke="#4f46e5"
              strokeWidth="2"
              strokeDasharray="6 4"
              opacity="0.4"
            />
            <line
              x1="425"
              y1="200"
              x2="425"
              y2="280"
              stroke="#6366f1"
              strokeWidth="2"
              strokeDasharray="6 4"
              opacity="0.4"
            />

            {/* Animated flow dots */}
            <circle r="4" fill="#818cf8">
              <animateMotion dur="2s" repeatCount="indefinite" path="M210,165 L310,145" />
            </circle>
            <circle r="4" fill="#818cf8">
              <animateMotion
                dur="2s"
                repeatCount="indefinite"
                path="M540,145 L640,165"
                begin="0.6s"
              />
            </circle>
            <circle r="4" fill="#a5b4fc">
              <animateMotion
                dur="2.5s"
                repeatCount="indefinite"
                path="M425,200 L425,280"
                begin="0.3s"
              />
            </circle>

            {/* Arrow labels */}
            <text x="255" y="140" textAnchor="middle" fill="#6366f1" fontSize="10" fontWeight="500">
              Request
            </text>
            <text x="595" y="140" textAnchor="middle" fill="#6366f1" fontSize="10" fontWeight="500">
              Forward
            </text>
            <text x="450" y="245" textAnchor="start" fill="#6366f1" fontSize="10" fontWeight="500">
              Govern
            </text>

            {/* Nodes */}
            {nodes.map((node) => (
              <g
                key={node.id}
                className="cursor-pointer"
                onClick={() => setActiveNode(activeNode === node.id ? null : node.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveNode(activeNode === node.id ? null : node.id);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`${node.label}: ${node.sublabel}. Click for details.`}
                aria-expanded={activeNode === node.id}
              >
                <rect
                  x={node.x}
                  y={node.y}
                  width={node.width}
                  height={node.height}
                  rx={14}
                  fill={activeNode === node.id ? '#3730a3' : node.color}
                  stroke={activeNode === node.id ? '#818cf8' : node.stroke}
                  strokeWidth={activeNode === node.id ? 2.5 : 1.5}
                  className="transition-all duration-200"
                />
                <text
                  x={node.x + node.width / 2}
                  y={node.y + node.height / 2 - 6}
                  textAnchor="middle"
                  fill="#e0e7ff"
                  fontSize="12"
                  fontWeight="700"
                >
                  {node.label}
                </text>
                <text
                  x={node.x + node.width / 2}
                  y={node.y + node.height / 2 + 14}
                  textAnchor="middle"
                  fill="#a5b4fc"
                  fontSize="9"
                >
                  {node.sublabel}
                </text>
              </g>
            ))}
          </svg>
        </div>

        {/* Detail card */}
        {active && (
          <div
            className="mt-8 mx-auto max-w-2xl rounded-2xl border border-indigo-500/30 bg-gray-900/80 p-6 backdrop-blur-sm"
            role="region"
            aria-live="polite"
            aria-label={`Details for ${active.label}`}
          >
            <h3 className="text-lg font-semibold text-white">{active.label}</h3>
            <p className="mt-2 text-sm text-gray-400 leading-relaxed">{active.detail}</p>
          </div>
        )}
      </div>
    </section>
  );
}
